'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Package {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice: number;
  items: string[];
  popular: boolean;
  savings: string;
}

interface PricingSectionProps {
  onSelectPackage: (packageId: string) => void;
}

const PricingSection = ({ onSelectPackage }: PricingSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const packages: Package[] = [
    {
      id: 'starter',
      name: 'Starter Pack',
      description: 'Perfect for trying our products',
      price: 799,
      originalPrice: 999,
      items: [
        '1 Mango Pickle (500g)',
        '1 Mixed Vegetable Pickle (500g)',
        '1 Amla Murraba (250g)',
      ],
      popular: false,
      savings: 'Save ₹200',
    },
    {
      id: 'family',
      name: 'Family Bundle',
      description: 'Most popular choice for families',
      price: 1899,
      originalPrice: 2499,
      items: [
        '2 Mango Pickle (500g each)',
        '1 Mixed Vegetable Pickle (500g)',
        '1 Lemon Pickle (500g)',
        '1 Pure Deshi Ghee (500g)',
        '1 Amla Murraba (500g)',
      ],
      popular: true,
      savings: 'Save ₹600',
    },
    {
      id: 'premium',
      name: 'Premium Collection',
      description: 'Complete traditional food experience',
      price: 3499,
      originalPrice: 4799,
      items: [
        '3 Mango Pickle (500g each)',
        '2 Mixed Vegetable Pickle (500g each)',
        '2 Lemon Pickle (500g each)',
        '2 Pure Deshi Ghee (1kg each)',
        '2 Amla Murraba (500g each)',
        '1 Carrot Murraba (500g)',
      ],
      popular: false,
      savings: 'Save ₹1300',
    },
  ];

  const handleSelectPackage = (packageId: string) => {
    if (!isHydrated) return;
    onSelectPackage(packageId);
  };

  return (
    <section id="pricing" className="bg-background py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-12 text-center">
          <h2 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
            Choose Your <span className="text-primary">Perfect Package</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Save more with our family bundles and bulk orders
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative overflow-hidden rounded-2xl bg-card shadow-cta transition-all duration-250 ease-out hover:shadow-lg ${
                pkg.popular ? 'ring-2 ring-primary' : ''
              }`}
            >
              {pkg.popular && (
                <div className="absolute left-0 right-0 top-0 bg-primary py-2 text-center">
                  <span className="font-cta text-sm font-bold text-primary-foreground">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`p-8 ${pkg.popular ? 'pt-14' : ''}`}>
                <h3 className="font-heading mb-2 text-2xl font-bold text-foreground">
                  {pkg.name}
                </h3>
                <p className="mb-6 text-sm text-muted-foreground">
                  {pkg.description}
                </p>

                <div className="mb-6">
                  <div className="mb-2 flex items-baseline gap-2">
                    <span className="font-heading text-4xl font-bold text-primary">
                      ₹{pkg.price}
                    </span>
                    <span className="text-lg text-muted-foreground line-through">
                      ₹{pkg.originalPrice}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1 rounded-full bg-success/10 px-3 py-1">
                    <Icon name="TagIcon" size={16} className="text-success" />
                    <span className="text-sm font-bold text-success">
                      {pkg.savings}
                    </span>
                  </div>
                </div>

                <div className="mb-6 space-y-3">
                  {pkg.items.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Icon
                        name="CheckCircleIcon"
                        size={20}
                        className="mt-0.5 flex-shrink-0 text-success"
                      />
                      <span className="text-sm text-foreground">{item}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleSelectPackage(pkg.id)}
                  className={`w-full rounded-lg px-6 py-4 font-cta text-base font-bold transition-all duration-250 ease-out ${
                    pkg.popular
                      ? 'bg-primary text-primary-foreground shadow-cta hover:shadow-lg'
                      : 'border-2 border-primary bg-background text-primary hover:bg-primary/10'
                  }`}
                >
                  Select Package
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-card p-6 text-center shadow-cta">
            <Icon
              name="TruckIcon"
              size={32}
              className="mx-auto mb-3 text-primary"
            />
            <h4 className="mb-1 font-bold text-foreground">Free Shipping</h4>
            <p className="text-sm text-muted-foreground">On orders above ₹500</p>
          </div>
          <div className="rounded-xl bg-card p-6 text-center shadow-cta">
            <Icon
              name="ShieldCheckIcon"
              size={32}
              className="mx-auto mb-3 text-primary"
            />
            <h4 className="mb-1 font-bold text-foreground">Quality Guarantee</h4>
            <p className="text-sm text-muted-foreground">100% satisfaction or refund</p>
          </div>
          <div className="rounded-xl bg-card p-6 text-center shadow-cta">
            <Icon
              name="GiftIcon"
              size={32}
              className="mx-auto mb-3 text-primary"
            />
            <h4 className="mb-1 font-bold text-foreground">Gift Packaging</h4>
            <p className="text-sm text-muted-foreground">Available for all orders</p>
          </div>
          <div className="rounded-xl bg-card p-6 text-center shadow-cta">
            <Icon
              name="ClockIcon"
              size={32}
              className="mx-auto mb-3 text-primary"
            />
            <h4 className="mb-1 font-bold text-foreground">Fresh Delivery</h4>
            <p className="text-sm text-muted-foreground">Within 3-5 business days</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;